import type { WorkerInboundMessage } from '../../workers/worker-protocol';
import {
  createMissionSpineRunId,
  recordMissionSpineMarker,
  type MissionSpineMarker,
} from './instrumentation';

export const OWNYOU_DATA_CHANGED_EVENT = 'ownyou:data-changed';

export interface OwnYouDataChangedDetail {
  namespace: string;
  sourceId: string;
  runId?: string;
}

export interface OwnYouDataChangedResult {
  dispatched: boolean;
  detail: OwnYouDataChangedDetail;
}

export interface ForwardDataChangeResult {
  forwarded: boolean;
  runId: string;
  namespace?: string;
  sourceId?: string;
  message?: Extract<WorkerInboundMessage, { type: 'NOTIFY_DATA_CHANGE' }>;
  markers: MissionSpineMarker[];
}

export interface MissionSpineWorkerPort {
  postMessage(message: Extract<WorkerInboundMessage, { type: 'NOTIFY_DATA_CHANGE' }>): void;
}

function browserWindow(): Window | null {
  return typeof window === 'undefined' ? null : window;
}

export function dispatchOwnYouDataChanged(
  detail: OwnYouDataChangedDetail,
  target: Pick<Window, 'dispatchEvent'> | null = browserWindow(),
): OwnYouDataChangedResult {
  if (!target) return { dispatched: false, detail };

  target.dispatchEvent(new CustomEvent(OWNYOU_DATA_CHANGED_EVENT, { detail }));
  return { dispatched: true, detail };
}

function ownYouDataChangedDetail(event: Event): Partial<OwnYouDataChangedDetail> {
  return ((event as CustomEvent<Partial<OwnYouDataChangedDetail>>).detail ?? {});
}

export function forwardOwnYouDataChangedToWorker(
  event: Event,
  worker: MissionSpineWorkerPort,
): ForwardDataChangeResult {
  const detail = ownYouDataChangedDetail(event);
  const runId = detail.runId ?? createMissionSpineRunId('bridge');
  const markers: MissionSpineMarker[] = [];

  if (!detail.namespace || !detail.sourceId) {
    markers.push(recordMissionSpineMarker({
      runId,
      hop: 'bridge_received',
      status: 'blocked',
      namespace: detail.namespace,
      sourceId: detail.sourceId,
      reasonCode: 'ownyou-data-changed-missing-detail',
      counts: { eventCount: 1 },
    }));
    return {
      forwarded: false,
      runId,
      namespace: detail.namespace,
      sourceId: detail.sourceId,
      markers,
    };
  }

  markers.push(recordMissionSpineMarker({
    runId,
    hop: 'bridge_received',
    status: 'success',
    namespace: detail.namespace,
    sourceId: detail.sourceId,
    reasonCode: 'ownyou-data-changed-received',
    counts: { eventCount: 1 },
  }));

  const message: Extract<WorkerInboundMessage, { type: 'NOTIFY_DATA_CHANGE' }> = {
    type: 'NOTIFY_DATA_CHANGE',
    payload: { namespace: detail.namespace, sourceId: detail.sourceId, runId },
  };
  worker.postMessage(message);

  markers.push(recordMissionSpineMarker({
    runId,
    hop: 'worker_notify_sent',
    status: 'success',
    namespace: detail.namespace,
    sourceId: detail.sourceId,
    reasonCode: 'notify-data-change-sent',
    counts: { messageCount: 1 },
  }));

  return {
    forwarded: true,
    runId,
    namespace: detail.namespace,
    sourceId: detail.sourceId,
    message,
    markers,
  };
}
